import { useState } from "react";
import { useI18n } from "@expertloop/i18n/react";
import { getLocaleDisplayName } from "@expertloop/i18n";
import type { MediationDecisionPreview } from "@expertloop/shared";
import { adminApi } from "../../services/api";

interface DecisionPreviewProps {
	orderId: string;
	message: string;
}

function asText(content: unknown): string {
	if (typeof content === "string") return content;
	const walk = (node: unknown): string => {
		if (!node || typeof node !== "object") return "";
		const n = node as { type?: string; text?: string; content?: unknown[] };
		if (n.type === "text" && typeof n.text === "string") return n.text;
		return Array.isArray(n.content) ? n.content.map(walk).join(" ") : "";
	};
	return walk(content);
}

/**
 * Aperçu du message du support dans la langue de chaque partie, avant l'envoi (LANG-3).
 * À la charge de la plateforme ; si le message est envoyé tel quel, la décision réutilise
 * cette traduction.
 */
export function DecisionPreview({ orderId, message }: DecisionPreviewProps) {
	const { t } = useI18n();
	const [preview, setPreview] = useState<MediationDecisionPreview | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const run = async () => {
		setLoading(true);
		setError("");
		try {
			setPreview(await adminApi.post<MediationDecisionPreview>(`/admin/mediations/${orderId}/preview-translation`, { message: message.trim() }));
		} catch {
			setPreview(null);
			setError(t("adminChatDetail.translation.previewFailed"));
		} finally {
			setLoading(false);
		}
	};

	const side = (label: string, entry: MediationDecisionPreview["expert"]) => (
		<div className="decision-preview-side">
			<strong>{label}</strong>
			<p>
				{entry.content !== null
					? asText(entry.content)
					: // Le message est déjà dans la langue de la partie : il n'y a rien à traduire, ce n'est pas une panne
						entry.reason === "understood"
						? t("adminChatDetail.translation.previewNotNeeded")
						: t("adminChatDetail.translation.previewUnavailable")}
			</p>
		</div>
	);

	return (
		<div className="decision-preview">
			<button type="button" className="btn btn-sm btn-outline" onClick={run} disabled={loading || !message.trim()}>
				{loading ? t("adminChatDetail.translation.previewing") : t("adminChatDetail.translation.previewButton")}
			</button>
			{error && <div className="resolution-error" role="alert">{error}</div>}
			{preview && (
				<div className="decision-preview-body" aria-live="polite">
					<strong>{t("adminChatDetail.translation.previewTitle")}</strong>
					{side(t("adminChatDetail.translation.previewExpert", { language: getLocaleDisplayName(preview.expert.locale, "en") }), preview.expert)}
					{side(t("adminChatDetail.translation.previewClient", { language: getLocaleDisplayName(preview.client.locale, "en") }), preview.client)}
					<span className="translation-indicative">{t("adminChatDetail.translation.indicative")}</span>
				</div>
			)}
		</div>
	);
}
